/**
 * PSLE cut-off point content adapter for school pages.
 *
 * What: Turns MOE's per-school posting-group data into a rendered table, the
 *       footnotes it has to be read with, and the bridge from an intake score
 *       into the Mathematics syllabus that posting group actually sits.
 * Why:  School pages already rank on page 1 for '<school> cut off point' and
 *       convert at well under 1%, because the snippet never contains the number
 *       the searcher came for. This module is that number — plus the framing
 *       that has to travel with it, since a tuition site publishing a wrong or
 *       naked cut-off is a trust problem before it is an SEO one.
 * When: Update when MOE changes what School Finder publishes, or when the
 *       posting-group → syllabus mapping changes.
 *
 * Every figure here is MOE's, read off School Finder — see
 * `shared/schools/schoolCutOffPoints.ts` and its generator. A school MOE
 * publishes no cut-off for has no entry, and this module returns null for it:
 * the page then renders no section at all rather than an empty state.
 */
import {
  CUT_OFF_PSLE_YEAR,
  CUT_OFF_S1_INTAKE_YEAR,
  CUT_OFF_SOURCE_NAME,
  CUT_OFF_SOURCE_URL,
  CUT_OFF_VERIFIED_ON,
  type CutOffRange,
  type HclGrade,
  POSTING_GROUP_LABELS,
  POSTING_GROUP_ORDER,
  type PostingGroupCutOffs,
  type PostingGroupKey,
  SCHOOL_CUT_OFF_POINTS,
  type SchoolCutOffPoints,
} from '@shared/schools/schoolCutOffPoints.js';
import type { SchoolVariant } from '@shared/schools/schoolVariants.js';

/** One admission route's figures for a single posting group. */
export interface SchoolCutOffCell {
  /** 'AL 4–7', or 'AL 21' when the first and last student scored the same. */
  range: string;
  /** The score the searcher means by "cut-off point" — the last student's. */
  cutOff: number;
  /** MOE's Higher Chinese tie-break note, present only for SAP schools. */
  note?: string;
  /**
   * True when MOE reported the weakest admissible score rather than a posted
   * student's, because the school still had vacancies. Rendering this as a
   * cut-off says the opposite of what happened, so it carries a footnote.
   */
  vacancyFloor?: boolean;
}

export interface SchoolCutOffRow {
  key: PostingGroupKey;
  /** 'Posting Group 3', 'Integrated Programme'. */
  label: string;
  nonAffiliated?: SchoolCutOffCell;
  affiliated?: SchoolCutOffCell;
}

export interface SchoolCutOffLink {
  label: string;
  path: string;
  description: string;
}

export interface SchoolCutOffContent {
  heading: string;
  intro: string;
  /** Column headers. `affiliated` is absent for schools with no affiliated intake. */
  columns: { group: string; nonAffiliated: string; affiliated?: string };
  rows: SchoolCutOffRow[];
  /** Marked footnotes, built from what the rows actually use. */
  footnotes: string[];
  /** Dated provenance — every surface moves together on the next refresh. */
  vintageLine: string;
  /** MOE's own caveats on reading a cut-off point as a threshold. */
  caveat: string;
  sourceLabel: string;
  sourceUrl: string;
  /** The conversion bridge: intake score → the syllabus your child will sit. */
  bridge: {
    title: string;
    body: string;
    links: SchoolCutOffLink[];
  };
}

const HCL_GRADE_LABELS: Record<HclGrade, string> = {
  D: 'Distinction',
  M: 'Merit',
  P: 'Pass',
};

/** Compact posting-group labels for the meta description's character budget. */
const SHORT_GROUP_LABELS: Record<PostingGroupKey, string> = {
  integratedProgramme: 'IP',
  postingGroup3: 'PG3',
  postingGroup2: 'PG2',
  postingGroup1: 'PG1',
};

/** Marker for the "school did not fill this posting group" footnote. */
const VACANCY_MARKER = '*';

const VACANCY_FOOTNOTE = `${VACANCY_MARKER} This posting group did not fill. ${CUT_OFF_SOURCE_NAME} then reports the cut-off as AL 30 — the weakest score the school could have admitted — rather than the score of a student who was actually posted there.`;

const HCL_FOOTNOTE = `Higher Chinese Language grades are published for Special Assistance Plan schools, which use them as an admission tie-break. Where a grade is missing, that student did not take or did not pass Higher Chinese.`;

/**
 * The Mathematics a posting group actually leads to, and where to read the
 * syllabus for it. Under Full Subject-Based Banding the posting group sets the
 * level a student takes each subject at by default — it is not a stream lock —
 * which is why every description below says "by default".
 */
const POSTING_GROUP_SYLLABUS: Record<
  PostingGroupKey,
  { label: string; path: string; description: string }
> = {
  integratedProgramme: {
    label: 'Secondary 1 (IP) Math syllabus',
    path: '/sec-1-ip',
    description:
      'IP students bypass the national secondary examination and work through an accelerated Mathematics course toward A-Level H2 Math.',
  },
  postingGroup3: {
    label: 'Secondary 1 G3 Math syllabus',
    path: '/sec-1-g3',
    description:
      'Posting Group 3 students take Mathematics at G3 by default — the fastest of the three, and the usual route into Additional Mathematics from Secondary 3.',
  },
  postingGroup2: {
    label: 'Secondary 1 G2 Math syllabus',
    path: '/sec-1-g2',
    description:
      'Posting Group 2 students take Mathematics at G2 by default, covering most of the G3 content at a steadier pace.',
  },
  postingGroup1: {
    label: 'Secondary 1 G1 Math syllabus',
    path: '/sec-1-g1',
    description:
      'Posting Group 1 students take Mathematics at G1 by default, building the foundations in smaller steps.',
  },
};

/** ACS(I) and SJI run the IP but examine at IB Diploma, so their IP row points at /ib. */
const IB_PROGRAMME_SYLLABUS = {
  label: 'Secondary 1 (IB) Math syllabus',
  path: '/sec-1-ib',
  description:
    'These students follow an integrated Secondary 1–4 course and are examined on the IB Diploma Mathematics curriculum rather than a national secondary examination.',
};

const SECONDARY_SYLLABUS_HUB: SchoolCutOffLink = {
  label: 'Secondary Math syllabus, all four years',
  path: '/secondary-math-syllabus',
  description:
    'The MOE topic list for every year and every subject level, so you can see where the syllabus goes after Secondary 1.',
};

/** ISO date → '12 August 2026', so the vintage line reads as prose. */
function formatVerifiedOn(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const monthName = MONTHS[(month ?? 1) - 1];
  if (!year || !monthName || !day) return iso;
  return `${day} ${monthName} ${year}`;
}

function formatHclNote(range: CutOffRange): string | undefined {
  const first = range.first.hclGrade;
  const last = range.last.hclGrade;
  if (!first && !last) return undefined;
  if (first && last) {
    return first === last
      ? `Higher Chinese ${HCL_GRADE_LABELS[first]}`
      : `Higher Chinese ${HCL_GRADE_LABELS[first]} to ${HCL_GRADE_LABELS[last]}`;
  }
  const graded = first ?? last;
  if (!graded) return undefined;
  const who = first ? 'first' : 'last';
  return `Higher Chinese ${HCL_GRADE_LABELS[graded]} (${who} student admitted)`;
}

function buildCell(range: CutOffRange): SchoolCutOffCell {
  const { first, last } = range;
  return {
    range:
      first.score === last.score
        ? `AL ${first.score}`
        : `AL ${first.score}–${last.score}`,
    cutOff: last.score,
    note: formatHclNote(range),
    vacancyFloor: range.unfilledVacancies === true ? true : undefined,
  };
}

function presentGroups(groups: PostingGroupCutOffs): PostingGroupKey[] {
  return POSTING_GROUP_ORDER.filter(key => groups[key] !== undefined);
}

/**
 * The number a searcher means by "<school> cut off point": the last student
 * posted through the most selective group the school admits into without
 * affiliation.
 *
 * A group the school did not fill is skipped — MOE's AL 30 there is a floor,
 * not a cut-off, and putting it in a title or a meta description would be a
 * plainly wrong number. A school whose every group went unfilled therefore has
 * no headline, and its meta stays on the tuition template.
 */
export function getHeadlineCutOff(
  cutOffs: SchoolCutOffPoints
): { key: PostingGroupKey; range: CutOffRange } | null {
  for (const key of POSTING_GROUP_ORDER) {
    const range = cutOffs.nonAffiliated[key];
    if (!range || range.unfilledVacancies) continue;
    return { key, range };
  }
  return null;
}

/** The COP record for a school, or undefined when MOE publishes none. */
export function getSchoolCutOffPoints(
  slug: string
): SchoolCutOffPoints | undefined {
  return SCHOOL_CUT_OFF_POINTS[slug];
}

/**
 * Compact per-group cut-offs for the meta description, e.g. 'PG3 22, PG2 25,
 * PG1 29'.
 *
 * Exactly one group carries its affiliated figure inline — "what is the
 * affiliated cut-off" is the second question every parent at an affiliated
 * school asks, but repeating it on all three blows the description's character
 * budget and costs the tuition clause. Unfilled groups are labelled as such
 * rather than dropped, since MOE's AL 30 there is a floor, not a cut-off.
 */
export function buildCutOffMetaSummary(cutOffs: SchoolCutOffPoints): string {
  const groups = presentGroups(cutOffs.nonAffiliated);
  const affiliatedKey = groups.find(key => {
    const affiliated = cutOffs.affiliated?.[key];
    return affiliated !== undefined && !affiliated.unfilledVacancies;
  });

  return groups
    .map(key => {
      const range = cutOffs.nonAffiliated[key];
      if (!range) return '';
      const short = SHORT_GROUP_LABELS[key];
      if (range.unfilledVacancies) return `${short} not filled`;
      const affiliated =
        key === affiliatedKey ? cutOffs.affiliated?.[key] : undefined;
      return affiliated
        ? `${short} ${range.last.score} (affiliated ${affiliated.last.score})`
        : `${short} ${range.last.score}`;
    })
    .filter(Boolean)
    .join(', ');
}

function buildBridge(
  schoolName: string,
  cutOffs: SchoolCutOffPoints,
  variant: SchoolVariant
): SchoolCutOffContent['bridge'] {
  // ACS(I) and SJI run one integrated Secondary 1–4 course and examine at IB
  // Diploma whichever posting group a student entered through, so every route
  // into them leads to the same syllabus — one link, and not the MOE hub, which
  // is the wrong curriculum for them.
  if (variant === 'ip-ib') {
    return {
      title: `What this intake means for the Maths your child will sit`,
      body: `${schoolName} admits through more than one posting group, but all of them lead into the same integrated Secondary 1–4 course — so the score above decides how a student enters, not which Mathematics they end up sitting.`,
      links: [IB_PROGRAMME_SYLLABUS],
    };
  }

  // A row present only on the affiliated side is still a route into the school,
  // so the bridge covers both admission routes.
  const keys = POSTING_GROUP_ORDER.filter(
    key =>
      cutOffs.nonAffiliated[key] !== undefined ||
      cutOffs.affiliated?.[key] !== undefined
  );

  return {
    title: `What this intake means for the Maths your child will sit`,
    body: `A posting group decides the level a student takes each subject at from Secondary 1, not only which class they are in — and under Full Subject-Based Banding a student who did well enough in a subject at PSLE can take that subject at a more demanding level than their posting group's default. So the score above is the start of the Mathematics your child sits at ${schoolName}, not the whole of it.`,
    links: [
      ...keys.map(key => POSTING_GROUP_SYLLABUS[key]),
      SECONDARY_SYLLABUS_HUB,
    ],
  };
}

/**
 * Build the cut-off section for a school page, or null when MOE publishes no
 * cut-off for the school — in which case the page renders no section at all.
 */
export function buildSchoolCutOffContent(
  slug: string,
  schoolName: string,
  variant: SchoolVariant
): SchoolCutOffContent | null {
  const cutOffs = getSchoolCutOffPoints(slug);
  if (!cutOffs) return null;

  const hasAffiliated = cutOffs.affiliated !== undefined;
  const keys = POSTING_GROUP_ORDER.filter(
    key =>
      cutOffs.nonAffiliated[key] !== undefined ||
      cutOffs.affiliated?.[key] !== undefined
  );
  if (keys.length === 0) return null;

  const rows: SchoolCutOffRow[] = keys.map(key => {
    const nonAffiliated = cutOffs.nonAffiliated[key];
    const affiliated = cutOffs.affiliated?.[key];
    return {
      key,
      label: POSTING_GROUP_LABELS[key],
      nonAffiliated: nonAffiliated ? buildCell(nonAffiliated) : undefined,
      affiliated: affiliated ? buildCell(affiliated) : undefined,
    };
  });

  const cells = rows.flatMap(row =>
    [row.nonAffiliated, row.affiliated].filter(
      (cell): cell is SchoolCutOffCell => cell !== undefined
    )
  );
  const footnotes: string[] = [];
  if (cells.some(cell => cell.vacancyFloor)) footnotes.push(VACANCY_FOOTNOTE);
  if (cells.some(cell => cell.note)) footnotes.push(HCL_FOOTNOTE);

  return {
    heading: `PSLE cut-off point for ${schoolName}`,
    intro: `MOE publishes the PSLE score of the first and the last student posted into each school at each posting group. Below are ${schoolName}'s figures for the ${CUT_OFF_S1_INTAKE_YEAR} Secondary 1 intake — the ${CUT_OFF_PSLE_YEAR} PSLE cohort. The last student's score is what is usually meant by the school's cut-off point.`,
    columns: {
      group: 'Posting group',
      nonAffiliated: hasAffiliated ? 'Non-affiliated' : 'PSLE score range',
      affiliated: hasAffiliated ? 'Affiliated' : undefined,
    },
    rows,
    footnotes,
    vintageLine: `Actual ${CUT_OFF_S1_INTAKE_YEAR} Secondary 1 intake, from ${CUT_OFF_SOURCE_NAME} — last verified on ${formatVerifiedOn(CUT_OFF_VERIFIED_ON)}.`,
    caveat: `Cut-off points move by a few points from year to year, and not every student who met the cut-off was posted here — some are tie-broken out. Read these as a guide to where a school sits, not as a threshold to clear.`,
    sourceLabel: CUT_OFF_SOURCE_NAME,
    sourceUrl: CUT_OFF_SOURCE_URL,
    bridge: buildBridge(schoolName, cutOffs, variant),
  };
}
